/**
 * Early Birds wishlist — localStorage store + UI components.
 */

export const WISHLIST_EVENT = 'eb:wishlist:update';
const STORAGE_KEY = 'ebWishlist';
const MAX_ITEMS = 50;

/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} image
 * @property {string} [price]
 * @property {string} [compareAtPrice]
 */

export class Wishlist {
  /** @returns {WishlistItem[]} */
  static getItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  /** @param {WishlistItem[]} items */
  static setItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    document.dispatchEvent(
      new CustomEvent(WISHLIST_EVENT, {
        detail: { count: items.length, items },
      })
    );
  }

  /** @param {string|number} id */
  static has(id) {
    const sid = String(id);
    return this.getItems().some((item) => String(item.id) === sid);
  }

  /**
   * @param {WishlistItem} item
   * @returns {boolean} true if now wishlisted
   */
  static toggle(item) {
    if (!item?.id) return false;
    const id = String(item.id);
    let items = this.getItems();
    const exists = items.some((entry) => String(entry.id) === id);

    if (exists) {
      items = items.filter((entry) => String(entry.id) !== id);
    } else {
      items.unshift({
        id,
        handle: item.handle || '',
        title: item.title || '',
        url: item.url || '',
        image: item.image || '',
        price: item.price || '',
        compareAtPrice: item.compareAtPrice || '',
      });
    }

    this.setItems(items);
    return !exists;
  }

  /** @param {string|number} id */
  static remove(id) {
    const sid = String(id);
    this.setItems(this.getItems().filter((item) => String(item.id) !== sid));
  }

  static count() {
    return this.getItems().length;
  }

  static clear() {
    this.setItems([]);
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.#sync();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(WISHLIST_EVENT, this.#onUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(WISHLIST_EVENT, this.#onUpdate);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const id = this.dataset.productId;
    if (!id) return;

    const active = Wishlist.toggle({
      id,
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '',
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
      compareAtPrice: this.dataset.productCompare || '',
    });

    this.#applyState(active);
  };

  #onUpdate = () => {
    this.#sync();
  };

  #sync() {
    const id = this.dataset.productId;
    this.#applyState(id ? Wishlist.has(id) : false);
  }

  /** @param {boolean} active */
  #applyState(active) {
    this.classList.toggle('is-active', active);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active
      ? this.dataset.labelRemove || 'Remove from wishlist'
      : this.dataset.labelAdd || 'Add to wishlist';
    this.setAttribute('aria-label', label);
    const sr = this.querySelector('[data-wishlist-label]');
    if (sr) sr.textContent = label;
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(WISHLIST_EVENT, this.#onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.#onUpdate);
  }

  #onUpdate = () => {
    this.#render();
  };

  #render() {
    const count = Wishlist.count();
    this.hidden = count === 0;
    this.textContent = count > 99 ? '99+' : String(count);
    this.classList.toggle('is-empty', count === 0);
  }
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(WISHLIST_EVENT, this.#onUpdate);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.#onUpdate);
    this.removeEventListener('click', this.#onClick);
  }

  #onUpdate = () => {
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const removeBtn = target.closest('[data-wishlist-remove]');
    if (removeBtn instanceof HTMLElement) {
      event.preventDefault();
      const id = removeBtn.dataset.wishlistRemove;
      if (id) Wishlist.remove(id);
      return;
    }

    const clearBtn = target.closest('[data-wishlist-clear]');
    if (clearBtn) {
      event.preventDefault();
      Wishlist.clear();
    }
  };

  #render() {
    const items = Wishlist.getItems();
    const empty = this.querySelector('[data-wishlist-empty]');
    const grid = this.querySelector('[data-wishlist-grid]');
    const countEl = this.querySelector('[data-wishlist-page-count]');
    const actions = this.querySelector('[data-wishlist-actions]');

    if (countEl) countEl.textContent = String(items.length);
    if (empty) empty.hidden = items.length > 0;
    if (actions) actions.hidden = items.length === 0;

    if (!grid) return;

    if (items.length === 0) {
      grid.innerHTML = '';
      grid.hidden = true;
      return;
    }

    grid.hidden = false;
    grid.innerHTML = items
      .map((item) => {
        const title = escapeHtml(item.title || 'Product');
        const url = escapeAttr(item.url || '#');
        const image = item.image
          ? `<img src="${escapeAttr(item.image)}" alt="${title}" width="600" height="750" loading="lazy" decoding="async">`
          : `<span class="eb-wishlist__placeholder" aria-hidden="true"></span>`;
        const price = item.price ? `<p class="eb-wishlist__price">${escapeHtml(item.price)}</p>` : '';

        return `
          <article class="eb-wishlist__card" data-product-id="${escapeAttr(item.id)}">
            <a class="eb-wishlist__media" href="${url}" aria-label="${title}">${image}</a>
            <div class="eb-wishlist__meta">
              <div class="eb-wishlist__top">
                <a class="eb-wishlist__title" href="${url}">${title}</a>
                <button type="button" class="eb-wishlist__remove" data-wishlist-remove="${escapeAttr(item.id)}" aria-label="Remove ${title} from wishlist">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M9.5 4.5h5M5.5 7h13M9 10.5v6M12 10.5v6M15 10.5v6M8.2 7l.7 11.2c.05.85.75 1.5 1.6 1.5h3c.85 0 1.55-.65 1.6-1.5L16 7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              ${price}
              <a class="eb-wishlist__view" href="${url}">
                View product
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </article>
        `;
      })
      .join('');
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value).replaceAll('`', '&#96;');
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
